/* eslint-disable @typescript-eslint/no-floating-promises */
import { Capacitor } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema,
  Token,
} from '@capacitor/push-notifications';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import { Opt } from 'app/tools/general.types';
import _ from 'lodash';
import { useSubscription } from 'observable-hooks';
import { isIOS } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import { BehaviorSubject, Subject } from 'rxjs';
import { share } from 'rxjs/operators';
import { getAppPlatform } from '../environment';

const NOTIFICATION_CHANNEL = 'new-chat-messages';

const relayNotification$ = new Subject<PushNotificationSchema>();
export const pushNotification$ = relayNotification$.pipe(share());

export const pushRegistration$ = new BehaviorSubject<Opt<Token>>(
  undefined
);

const relayNotifAction$ = new Subject<ActionPerformed>();
export const pushNotifAction$ = relayNotifAction$.pipe(share());

export const getChatIdFromNotification = ({
  data,
}: PushNotificationSchema): Opt<string> => {
  const chatId: unknown = _.get(data, 'chatId');
  return chatId ? String(chatId) : undefined;
};

export function useNavToChatOnNotificationClick(): void {
  const history = useHistory();
  useSubscription(pushNotifAction$, action => {
    const chatId = getChatIdFromNotification(action.notification);
    if (chatId) {
      history.push(`${SharedRoutePath.CHAT}/${chatId}`);
    }
  });
}

/**
 * To be used as a React Component to manage foreground in-app notifications
 * Must be placed inside the ShowInfoProvider to work properly.
 * @returns null
 */
export function InAppNotifications(): null {
  const { showSnack } = useShowInfo();

  const showNotificationAsSnackbar = (
    notification: PushNotificationSchema
  ) => {
    const showActionButton = !_.isEmpty(notification.data);
    showSnack({
      message: notification.title || 'New notification',
      action: showActionButton
        ? {
            text: 'View',
            callback: () =>
              relayNotifAction$.next({
                actionId: _.random(10000, 99999, false).toString(),
                notification,
              }),
          }
        : undefined,
    });
  };

  useSubscription(pushNotification$, notification => {
    const chatId = getChatIdFromNotification(notification) || '';
    const onChatPage =
      window.location.pathname ===
      `${SharedRoutePath.CHAT}/${chatId}`;
    // NOTE: not showing in app notifications on apple because on
    // ios the native notifications show even when app in foreground.
    if (!isIOS && !onChatPage) {
      showNotificationAsSnackbar(notification);
    }
  });
  return null;
}

export function initNotifications(
  onRegistrationError: (error: unknown) => void
): void {
  if (!Capacitor.isNativePlatform()) return;

  PushNotifications.requestPermissions().then(permission => {
    if (permission.receive === 'granted') {
      // Register with Apple / Google to receive push via APNS/FCM
      PushNotifications.register();
    } else {
      // No permission for push granted
    }
  });

  if (getAppPlatform() === 'android') {
    PushNotifications.createChannel({
      id: NOTIFICATION_CHANNEL,
      name: 'New chat messages',
      importance: 5,
    });
  }

  PushNotifications.removeAllListeners();

  PushNotifications.addListener('registration', token =>
    pushRegistration$.next(token)
  );
  PushNotifications.addListener(
    'registrationError',
    onRegistrationError
  );
  PushNotifications.addListener(
    'pushNotificationReceived',
    notification => relayNotification$.next(notification)
  );
  PushNotifications.addListener(
    'pushNotificationActionPerformed',
    action => relayNotifAction$.next(action)
  );
}
